import React from 'react';
import * as Sentry from '@sentry/react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import BottomNav from './BottomNav';

/**
 * RouteErrorBoundary
 *
 * Per-route error boundary that contains a render-time exception to the
 * affected tab instead of taking down the entire app. The app-level
 * `Sentry.ErrorBoundary` in App.js is the last-resort fallback ("Return
 * to Home"); this one sits inside ProtectedRoute so:
 *
 *   - If Plan crashes, Plan shows a retry card and the BottomNav stays
 *     interactive so the user can switch to Home/Progress/Settings
 *     without restarting the app.
 *   - The exception still ships to Sentry via `Sentry.ErrorBoundary`'s
 *     built-in reporting, so we don't lose telemetry.
 *
 * Renders nothing extra during normal operation (children render as-is).
 */
function RouteErrorFallback({ resetError }) {
  return (
    <div
      className="min-h-screen pb-bottom-nav bg-background flex flex-col"
      data-testid="route-error-boundary"
    >
      <div className="flex-1 flex items-center justify-center px-6">
        <div className="max-w-sm w-full text-center space-y-4">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-amber-50 text-amber-600">
            <AlertTriangle size={28} aria-hidden="true" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900">
            Something went wrong on this screen
          </h2>
          <p className="text-sm text-gray-600">
            We've logged the issue. You can retry this tab or switch to another tab below.
          </p>
          <button
            type="button"
            onClick={resetError}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gray-900 text-white text-sm hover:bg-gray-800 transition"
            data-testid="route-error-retry"
          >
            <RefreshCw size={16} aria-hidden="true" />
            Try again
          </button>
        </div>
      </div>
      <BottomNav />
    </div>
  );
}

export default function RouteErrorBoundary({ children }) {
  return (
    <Sentry.ErrorBoundary
      fallback={(props) => <RouteErrorFallback resetError={props.resetError} />}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}
